import config from 'constants/config'
import { formatPlacePhrase } from '@luxuryescapes/lib-location-grammar'
import { getDestinationImage } from 'lib/search/destinationImageMap'
import type { IntlShape } from 'react-intl'

interface ExperiencesHeadMetaParams {
  placeName?: string
  placeId?: string
  isAnywhere: boolean
  intl: IntlShape
}

interface ExperiencesHeadMeta {
  title: string
  description: string
  imageId?: string
  imageAlt: string
}

export function getExperiencesSearchHeadMeta({ placeName, placeId, isAnywhere, intl }: ExperiencesHeadMetaParams): ExperiencesHeadMeta {
  const effectivePlaceName = placeName?.trim()
  const hasPlace = !!effectivePlaceName && !isAnywhere
  const placeLocationPhrase = effectivePlaceName ? formatPlacePhrase({ location: { name: effectivePlaceName }, language: intl.locale, intent: 'location' }) : ''

  const title = hasPlace
    ? intl.formatMessage(
        {
          id: 'QEXXKigOzN51_MogxBx0',
          defaultMessage: 'Browse our best handpicked experiences in {placeName}',
          description: 'SEO page title for experience search results with a specific place',
        },
        { placeName: placeLocationPhrase },
      )
    : intl.formatMessage({
        id: '0_LhNOYLR8pZnkyzU8wp',
        defaultMessage: 'Browse our best handpicked experiences',
        description: 'SEO page title for experience search results without a specific place',
      })

  const description = hasPlace
    ? intl.formatMessage(
        {
          id: '5T3TGFY2j8WoDIEpgDJd',
          defaultMessage: 'Discover unforgettable experiences in {placeName} with {brandName}.',
          description: 'SEO meta description for experience search results with a specific place',
        },
        { placeName: placeLocationPhrase, brandName: config.title },
      )
    : intl.formatMessage(
        {
          id: '0PWuY-8MVawzjauD31tn',
          defaultMessage: 'Discover unforgettable experiences with {brandName}.',
          description: 'SEO meta description for experience search results without a specific place',
        },
        { brandName: config.title },
      )

  const imageAlt = hasPlace
    ? intl.formatMessage(
        {
          id: '7ZcmXki2I3igOLz-Uz-V',
          defaultMessage: '{placeName} experiences',
          description: 'Alt text for SEO image on experience search results with a specific place',
        },
        { placeName: effectivePlaceName },
      )
    : intl.formatMessage(
        {
          id: 'w7XMwZvHhDgqq-Mrz-2Y',
          defaultMessage: '{brandName} experiences',
          description: 'Alt text for SEO image on experience search results without a specific place',
        },
        { brandName: config.title },
      )

  return {
    title,
    description,
    imageId: !isAnywhere && placeId ? getDestinationImage(placeId) : undefined,
    imageAlt,
  }
}
